import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { OrganizationStateService } from './organization-state.service';
import { environment } from '../../environments/environment';
import { SmartAppEnvironmentDto } from '../models/smartApp-environment-dto';
import { DataExportVersionDto } from '../models/dataExport-version-dto';
import { PaginatedResponse } from '../models/paginated-response';

@Injectable({
  providedIn: 'root'
})
export class OrganizationService {
  private baseUrl = `${environment.smartPortalApiUrl}Organization`;


  private organizationIdSubject = new BehaviorSubject<string | null>(localStorage.getItem('selectedOrganizationId'));
  organizationId$ = this.organizationIdSubject.asObservable();

  private isDepartmentEntryCompleteSubject = new BehaviorSubject<boolean>(false);
  isDepartmentEntryComplete$ = this.isDepartmentEntryCompleteSubject.asObservable();


  constructor(private httpClient: HttpClient, private organizationState: OrganizationStateService) {
    const storedOrgId = this.organizationState.currentOrganizationId;
    if (storedOrgId) {
      this.setOrganizationId(storedOrgId);
    }
  }

  getAccessibleOrganizations(): Observable<any> {
    return this.httpClient.get(`${this.baseUrl}/accessible`);
  }

  getOrganizations(): Observable<any> {
    return this.getAccessibleOrganizations();
  }

  getOrganization(): Observable<any> {
    return this.httpClient.get(`${this.baseUrl}/Client/`);
  }

  setOrganizationId(id: string | null) {
    this.organizationState.setOrganizationId(id);
    this.organizationIdSubject.next(id);

    if (id) {
      this.loadIsDepartmentEntryComplete(id);
    }
  }

  private loadIsDepartmentEntryComplete(organizationId: string) {
    this.httpClient.get<{ isDepartmentEntryComplete: boolean }>(`${this.baseUrl}/${organizationId}`)
      .pipe(
        tap(response => {
          this.isDepartmentEntryCompleteSubject.next(response.isDepartmentEntryComplete);
        })
      ).subscribe();
  }

  get currentOrganizationId(): string | null {
    return this.organizationState.currentOrganizationId;
  }

  setIsDepartmentEntryComplete(value: boolean): void {
    this.isDepartmentEntryCompleteSubject.next(value);
  }

  toggleIsDepartmentEntryComplete(): Observable<boolean> {
    return this.httpClient.patch<boolean>(`${this.baseUrl}/${this.currentOrganizationId}/toggle-department-entry-complete`, {})
      .pipe(tap(value => this.setIsDepartmentEntryComplete(value)));
  }


  // smart app environments
  updateExternalEnvironment(environmentId: string, env: Partial<SmartAppEnvironmentDto>): Observable<void> {
    return this.httpClient.put<void>(`${this.baseUrl}/Client/Environment/${environmentId}`, env);
  }

  getExportHistory(datasetType?: string, page: number = 1, pageSize: number = 10): Observable<PaginatedResponse<DataExportVersionDto>> {
    let url = `${this.baseUrl}/export-history?page=${page}&pageSize=${pageSize}`;
    if (datasetType) {
      url += `&datasetType=${encodeURIComponent(datasetType)}`;
    }
    return this.httpClient.get<PaginatedResponse<DataExportVersionDto>>(url);
  }

  promoteData(organizationId: string, externalEnvironmentId: string, datasetType: string, notes?: string): Observable<void> {

    let numericDatasetType: DatasetType;
    switch (datasetType) {
      case 'Department':
        numericDatasetType = DatasetType.Department;
        break;
      case 'KbItem':
      case 'Knowledge Base':
        numericDatasetType = DatasetType.KbItem;
        break;
      default:
        throw new Error(`Invalid DatasetType: ${datasetType}`);
    }


    const body = { organizationId, environmentId: externalEnvironmentId, datasetType: numericDatasetType, notes };
    return this.httpClient.post<void>(`${this.baseUrl}/promote-data`, body);
  }
}

export enum DatasetType {
  Department = 0,
  KbItem = 1
}